import React, { useRef, useReducer, useEffect, useState } from 'react';

import { install } from 'resize-observer';
import ReactSlider from 'react-slider';

import createStyle from 'shared-utils/src/createStyle';
import makeClassName from 'shared-utils/src/makeClassName';
import integerWithThousandSeparator from 'shared-utils/src/integerWithThousandSeparator';

import Grid from '../Grid';
import GridItem from '../GridItem';

import {
  reducer,
  getValueIndex,
  getInitialSliderValues,
  getValuesOnSliderChange,
} from './utils';

import styles, { inputCls, sliderCls } from './styles';

const useStyles = createStyle(styles);

const RangeSlider = ({
  filter,
  minValue,
  maxValue,
  values,
  measureUnit,
  updateValue,
  isMobile,
  minDistance,
  pearling,
  placeholders = null,
  disableFromConf = false,
}) => {
  const cls = useStyles();

  const minInput = useRef(null);
  const maxInput = useRef(null);

  const [pl] = useState(placeholders || { min: '- -', max: '- -' })

  const [state, dispatch] = useReducer(reducer, {
    slider: getInitialSliderValues(minValue, maxValue, values),
    inputs: { min: integerWithThousandSeparator(parseInt(minValue, 10)) || '', max: integerWithThousandSeparator(parseInt(maxValue, 10)) || '' },
    focus: { min: false, max: false },
  });

  useEffect(() => {
    if (!window.ResizeObserver) {
      install();
    }
  }, []);

  useEffect(() => {
    if (
      minValue === null
      && maxValue === null
    ) {
      dispatch({
        type: 'onsliderchange',
        payload: {
          slider: [null, values.length],
          inputs: { min: '', max: '' },
        },
      });
    } else {
      const inputMin = minValue
       ? integerWithThousandSeparator(parseInt(minValue, 10))
       : '';
      const inputMax = maxValue
       ? integerWithThousandSeparator(parseInt(maxValue, 10))
       : '';

      dispatch({
        type: 'onsliderchange',
        payload: {
          slider: getInitialSliderValues(minValue, maxValue, values),
          inputs: { min: inputMin || '', max: inputMax || '' },
        },
      });
    }
  }, [minValue, maxValue]);

  const onSliderChange = (newValues) => {
    const { newInputsValues, newSliderValues } = getValuesOnSliderChange(newValues, values, state);
    dispatch({
      type: 'onsliderchange',
      payload: {
        slider: newSliderValues,
        inputs: newInputsValues,
      },
    });
  };

  const onSliderAfterChange = (newValues) => {
    const { newInputsValues, newSliderValues } = getValuesOnSliderChange(newValues, values, state);
    dispatch({
      type: 'onsliderchange',
      payload: {
        slider: newSliderValues,
        inputs: newInputsValues,
      },
    });
    updateValue({
      [`${filter}Min`]: newInputsValues.min ? newInputsValues.min.replace(/\./g, '') : undefined,
      [`${filter}Max`]: newInputsValues.max ? newInputsValues.max.replace(/\./g, '') : undefined,
    });
  };

  const onInputFocus = (e) => {
    const { target } = e;
    const newFocusState = target === minInput.current
      ? { min: true, max: false }
      : { min: false, max: true };
    dispatch({
      type: 'oninputfocus',
      payload: {
        focus: newFocusState,
      },
    });
  };

  const onInputChange = (e) => {
    const { target, target: { value } } = e;
    const newState = {};
    if (target === minInput.current) {      
      newState.min = integerWithThousandSeparator(parseInt(value.replace(/\./g, ''), 10));
      newState.max = state.inputs.max;
    } else if (target === maxInput.current) {
      newState.min = state.inputs.min;
      newState.max = integerWithThousandSeparator(parseInt(value.replace(/\./g, ''), 10));
    }
    dispatch({
      type: 'oninputchange',
      payload: {
        inputs: newState,
      },
    });
  };

  const onInputBlur = (e) => {
    const { target } = e;
    let  value = target === minInput.current
      ? state.inputs.min
      : state.inputs.max;
      

    const newIndex = getValueIndex(value, values);

    const newState = [];

    newState[0] = target === minInput.current
      ? newIndex
      : state.slider[0];

    newState[1] = target === maxInput.current
      ? newIndex
      : state.slider[1];  
      
    let newFocusState;
    if (target === minInput.current) {
      newFocusState = { ...state.focus, min: false };
    } else {
      newFocusState = { ...state.focus, max: false };
    }

    const slider = value
      ? newState
      : null;

    dispatch({
      type: 'oninputblur',
      payload: {
        focus: newFocusState,
        slider,
      },
    });

    updateValue({
      [`${filter}Min`]: state.inputs.min ? state.inputs.min.replace(/\./g, '') : undefined,
      [`${filter}Max`]: state.inputs.max ? state.inputs.max.replace(/\./g, '') : undefined,
    });
  };

  const handleKeyboardEvents = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.target.blur();
    }
    if (
      Number.isNaN(parseInt(event.key, 10)) &&
      event.key !== 'Tab' &&
      event.key !== 'Backspace' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight'
    ) {
      event.preventDefault();
    }
  };

  const inputContCls = isMin => makeClassName([
    cls[inputCls],
    'pr--s pl--s tp-s--s tp-w--m c-txt--f2 c-bg--w b-r--m',
    (isMin && state.focus.min) && 'infocus',
    (!isMin && state.focus.max) && 'infocus',
  ]);

  const inputFieldCls = makeClassName([
    cls[`${inputCls}__input`],
    'tp-s--l tp-w--m tp-a--c c-txt--secondary c-bg--w',
  ]);
  const inputBoxCls = makeClassName([
    cls[`${inputCls}__half`],
    disableFromConf && cls[`${inputCls}__half__disabled`],
  ]);

  const rangeCustomCls = `${sliderCls}${state.inputs.min || state.inputs.max ? ' active' : ''}${disableFromConf ? ` ${sliderCls}__disabled` : ''}`;
  return (
    <Grid direction="column">
      <GridItem>
        <Grid
          className="mb--m"
          justify="space-between"
          noWrap
        >
          <GridItem
            behavior="fixed"
            className={inputBoxCls}
          >
            <div className={inputContCls(1)}>
              Da
              <input
                ref={minInput}
                disabled={disableFromConf}
                className={inputFieldCls}
                type={isMobile ? 'tel' : 'text'}
                placeholder={pl.min}
                value={state.inputs.min || ''}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                onChange={onInputChange}
                onKeyDown={isMobile ? null : handleKeyboardEvents}
              />
              {measureUnit}
            </div>
          </GridItem>
          <GridItem
            behavior="fixed"
            className={inputBoxCls}
          >
          <div className={inputContCls(0)}>
              A
              <input
                ref={maxInput}
                disabled={disableFromConf}
                className={inputFieldCls}
                type={isMobile ? 'tel' : 'text'}
                placeholder={pl.max}
                value={state.inputs.max || ''}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                onChange={onInputChange}
                onKeyDown={isMobile ? null : handleKeyboardEvents}
              />
              {measureUnit}
            </div>
          </GridItem>
        </Grid>
      </GridItem>
      <GridItem>
        {window.ResizeObserver && (
          <ReactSlider
            className={rangeCustomCls}
            thumbClassName={cls[`${sliderCls}__handle`]}
            trackClassName={cls[`${sliderCls}__track`]}
            value={state.slider}
            min={0}    
            max={values.length}
            onChange={onSliderChange}
            onAfterChange={onSliderAfterChange}          
            minDistance={minDistance}
            pearling={pearling}
            disabled={disableFromConf}
          /> 
        )}
      </GridItem>
    </Grid>
  );
};

export default RangeSlider;
